import {
  getAccommodationPageHotelId,
  getRoomsActiveFilters
} from 'modules/accommodation/selectors'
import {isVioOfferOrBundleSelected} from 'modules/accommodation/utils'
import {
  getNumberOfGuests,
  getNumberOfRooms,
  getOfferAccessFlowSettings,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {HotelOfferEntity} from 'modules/sapiSearch/slice'
import {getIsAnchorHotelHidden} from 'modules/search/selectors'
import {
  getFilteredRoomsData,
  getOffersSearchContext,
  getRoomsResult
} from 'modules/searchApi/selectors'
import {once} from 'ramda'
import {Dispatch} from 'redux'
import {RootState} from 'store'
import {getBridgedFormatMessage} from 'utils/localization/formatMessageBridge'

import {
  logTimeEnd,
  logTimeStart
} from '@vio/core/src/_web/utils/logging/performanceTimer'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Page,
  SearchContext,
  Team
} from '@vio/core/src/analytics/types/Events'
import {getShouldSeeOffersUnlocked} from '@vio/core/src/auth/modules/selectors'
import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {offersHasPrivateDeal} from '@vio/core/src/offer/business/privateDeals'
import {OfferTagLabel} from '@vio/core/src/offer/types/offer'
import {roomsFilterListToMap} from '@vio/core/src/room/business/utils/filters'
import {
  faqFetchSucceeded,
  offersSearchErrored,
  offersSearchReceived,
  offersSearchStarted,
  offersSearchSucceeded,
  roomSearchErrored,
  roomSearchStarted,
  roomSearchSucceeded
} from '@vio/core/src/sapi/services/searchApi/action'
import {getRoomDisplayName} from '@vio/shared/src/accommodation/RoomDisplayName'

import {MiddlewareType} from '../middlewareList'
import {trackTimingEvent} from '.'
import {buildOfferContext} from './contexts/offerContext'
import {
  getHotelContext,
  getIsRoomsSearchManuallyTriggered,
  getPaginationContext,
  getRoomContext,
  getSearchContext,
  getSearchResultsContext,
  getTopOffersOfferContexts
} from './selectors'
import {getRoomsOffersMatchingData} from './utils/getRoomsOffersMatchingData'
import {logEvent} from './utils/logEvent'

const ROOM_SEARCH_SOURCE_COMPONENT = 'HotelViewRoomsSection'
const ROOM_SEARCH_APPLIED_FILTER = 'ap_roomsFilter'

enum MatchFlow {
  Other = 'Other',
  VioClicker = 'vioOfferClicker',
  Refresh = 'PageRefresh'
}

const getAllOffersContexts = (
  state: RootState,
  offerEntity: HotelOfferEntity | undefined,
  hotelId?: string
) => {
  const allOffers = (offerEntity?.offers ?? []).map(offer => {
    return buildOfferContext(
      offer.id,
      '',
      offerEntity,
      offer,
      getNumberOfRooms(state, hotelId),
      getShouldSeeOffersUnlocked(state),
      getSplitBookingDetails(state, offerEntity?.id),
      getOfferAccessFlowSettings(state, offerEntity?.id, offer?.id)
    )
  })
  return allOffers
}

const searchResultsContextForOffers = (
  state: RootState,
  offers: HotelOfferEntity | undefined
) => {
  const shouldSeeOffersUnlocked = getShouldSeeOffersUnlocked(state)
  const anchorHotelHasPrivateDeal = offersHasPrivateDeal(
    offers?.offers,
    shouldSeeOffersUnlocked
  )
  const anchorHotelHasYourChoice = Boolean(
    offers?.offers?.some(offer =>
      hasOfferTag(offer, OfferTagLabel.IsYourChoice)
    )
  )
  const hotelId = getAccommodationPageHotelId(state)

  const hotelsMetaData = [
    {
      ...getHotelContext(state, hotelId),
      // get top offers from sapiSearch state
      topOffers: getTopOffersOfferContexts(state, hotelId),
      // get all offers from searchApi search result
      allOffers: getAllOffersContexts(state, offers, hotelId)
    }
  ]

  return {
    algoliaNbHotels: 1,
    algoliaTotalNbHotels: 1,
    anchorHotelHasPrivateDeal,
    anchorHotelHasYourChoice,
    hotelIndexesWithPrivateDeals: anchorHotelHasPrivateDeal
      ? [state.accommodation.hotelId]
      : [],
    hotelsMetaData,
    nbHotels: 1,
    staticHotelsLength: 1,
    staticHotelsOffset: 0,
    unavailableHotels: null, // this relates only to hotel list which we don't have here
    pageNumber: 1,
    pageOffset: 0,
    pageLength: 1
  }
}

const trackOffersSearchRecieved = once((dispatch: Dispatch) => {
  trackTimingEvent(
    dispatch,
    'OffersSearchRecieved',
    'offersSearch-start',
    Entity.Search,
    Team.Select
  )
})

const trackOffersSearchRecievedTiming = (dispatch: Dispatch) => {
  trackTimingEvent(
    dispatch,
    'OffersReceived',
    'offersSearch-start',
    Entity.Search,
    Team.Select
  )
}

const trackAllRoomOffersReceivedAbsoluteTime = (dispatch: Dispatch) => {
  trackTimingEvent(
    dispatch,
    'AllRoomOffersReceivedAbsoluteTime',
    'responseStart',
    Entity.RoomSearch,
    Team.Select
  )
}

const middleware: MiddlewareType = store => next => async action => {
  const state = store.getState()
  const {dispatch} = store
  switch (action.type) {
    case offersSearchStarted.type: {
      const {parameters, searchId} = action.payload
      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const searchContext: SearchContext = {
        ...getSearchContext(state, searchId),
        ...getOffersSearchContext(state),
        ...(parameters.getAllOffers ? {isUserSearch: false} : {})
      }

      logEvent('OffersSearch', parameters)
      logTimeStart('offersSearch')
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Requested,
          analyticsContext: {
            [AnalyticsContext.PaginationContext]: getPaginationContext(state),
            [AnalyticsContext.SearchContext]: searchContext
          },
          team: Team.Select
        })
      )
      break
    }

    case offersSearchReceived.type: {
      trackOffersSearchRecieved(dispatch)
      break
    }

    case offersSearchSucceeded.type: {
      const {offers, parameters, searchId} = action.payload
      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const searchContext: SearchContext = {
        ...getSearchContext(state, searchId),
        ...getOffersSearchContext(state),
        ...(parameters.getAllOffers ? {isUserSearch: false} : {})
      }

      trackOffersSearchRecievedTiming(dispatch)
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Succeeded,
          analyticsContext: {
            [AnalyticsContext.PaginationContext]: getPaginationContext(state),
            [AnalyticsContext.SearchContext]: searchContext,
            [AnalyticsContext.SearchResultsContext]:
              searchResultsContextForOffers(state, offers)
          },
          payload: {
            // currently viewed hotel is considered as "anchor" and its availability is based on found offers.
            // SAPI can complete polling without an offer entity, so `offers` may be undefined.
            isAnchorHotelAvailable: (offers?.offers?.length ?? 0) > 0,
            isAnchorHotelHidden: getIsAnchorHotelHidden(state)
          },
          team: Team.Select
        })
      )
      break
    }

    case offersSearchErrored.type: {
      // TODO: Search TS preexisting issue
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const searchContext: SearchContext = {
        ...getSearchContext(state),
        ...getOffersSearchContext(state)
      }

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Errored,
          analyticsContext: {
            [AnalyticsContext.PaginationContext]: getPaginationContext(state),
            [AnalyticsContext.SearchContext]: searchContext
          },
          team: Team.Select
        })
      )
      break
    }

    case roomSearchStarted.type: {
      const activeRoomsFilters = getRoomsActiveFilters(state)
      const isUserSearch = getIsRoomsSearchManuallyTriggered(state)

      const urlParams = window
        ? new URLSearchParams(window.location.search)
        : null
      const redirectId = urlParams?.get('redirectId') || undefined

      // We only want to start timers for events which are triggered by real SAPI.rooms call
      // Events triggered without calling SAPI.rooms should not be "timed" (f.e. when using rooms filters)
      const roomsRequestId = action.payload?.roomsRequestId
      if (roomsRequestId) {
        logTimeStart(`${Entity.RoomSearch}-${roomsRequestId}`)
      }

      const component =
        activeRoomsFilters.length > 0
          ? ROOM_SEARCH_APPLIED_FILTER
          : ROOM_SEARCH_SOURCE_COMPONENT
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.RoomSearch,
          action: Action.Requested,
          component,
          page: Page.Accommodation,
          payload: {
            isUserSearch,
            redirectId
          },
          team: Team.Select
        })
      )
      break
    }

    case roomSearchSucceeded.type: {
      const activeRoomsFilters = getRoomsActiveFilters(state)
      const isUserSearch = getIsRoomsSearchManuallyTriggered(state)

      const urlParams = window
        ? new URLSearchParams(window.location.search)
        : null
      const redirectId = urlParams?.get('redirectId') || undefined
      const srpSearchId = urlParams?.get('srpSearchId') || undefined
      const isRoomUserSearch = urlParams?.get('roomUserSearch') === '1'

      const roomsRequestId = action.payload?.roomsRequestId
      const isRefreshing = action.payload?.isRefreshing

      const roomSearchDuration = roomsRequestId
        ? logTimeEnd(`${Entity.RoomSearch}-${roomsRequestId}`)
        : undefined

      const {data: roomsData} = getRoomsResult(state) || {}

      const {splitBooking: stateSplitBooking} = roomsData || {}
      const {rooms, totalResults, availableFilters, splitBooking} =
        getFilteredRoomsData(state)

      const component =
        activeRoomsFilters.length > 0
          ? ROOM_SEARCH_APPLIED_FILTER
          : ROOM_SEARCH_SOURCE_COMPONENT

      const matchingProps = getRoomsOffersMatchingData(rooms, splitBooking)

      const matchFlow = isRefreshing
        ? MatchFlow.Refresh
        : isVioOfferOrBundleSelected()
          ? MatchFlow.VioClicker
          : MatchFlow.Other

      const payload = {
        redirectId,
        isUserSearch,
        isRoomUserSearch,
        isAnyRoomAvailable: !!totalResults,
        roomFiltersDisplayed: availableFilters,
        roomFiltersApplied: roomsFilterListToMap(activeRoomsFilters),
        roomSearchDuration,
        ...matchingProps,
        matchFlow,
        roomSearchId: roomsData?.searchId,
        srpSearchId
      }

      trackAllRoomOffersReceivedAbsoluteTime(dispatch)

      const formatMessage = getBridgedFormatMessage()
      const occupancy = {
        numberOfGuests: getNumberOfGuests(state),
        numberOfRooms: getNumberOfRooms(
          state,
          getAccommodationPageHotelId(state)
        )
      }

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.RoomSearch,
          action: Action.Succeeded,
          component,
          page: Page.Accommodation,
          payload,
          team: Team.Select,
          analyticsContext: {
            [AnalyticsContext.SearchResultsContext]:
              getSearchResultsContext(state),
            [AnalyticsContext.RoomContextList]: rooms.map(room => {
              const roomContext = getRoomContext(state, room.id, rooms)
              if (!roomContext || !formatMessage) return roomContext
              return {
                ...roomContext,
                displayRoomName: getRoomDisplayName(
                  formatMessage,
                  room.name,
                  occupancy
                )
              }
            }),
            [AnalyticsContext.SplitBooking]: stateSplitBooking
              ? {
                  ...stateSplitBooking,
                  isHidden: !splitBooking,
                  roomPosition: 1
                }
              : null
          }
        })
      )
      break
    }

    case roomSearchErrored.type: {
      const activeRoomsFilters = getRoomsActiveFilters(state)
      const isUserSearch = getIsRoomsSearchManuallyTriggered(state)

      const urlParams = window
        ? new URLSearchParams(window.location.search)
        : null
      const redirectId = urlParams?.get('redirectId') || undefined

      const component =
        activeRoomsFilters.length > 0
          ? ROOM_SEARCH_APPLIED_FILTER
          : ROOM_SEARCH_SOURCE_COMPONENT

      const payload = {
        redirectId,
        isUserSearch
      }

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.RoomSearch,
          action: Action.Errored,
          component,
          page: Page.Accommodation,
          payload,
          team: Team.Select
        })
      )
      break
    }

    case faqFetchSucceeded.type: {
      const {payload} = action
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.FAQFetch,
          action: Action.Succeeded,
          payload
        })
      )
      break
    }

    default: {
      break
    }
  }

  return next(action)
}

export default middleware
